// Lookup2DTablesTab.js - Matrix lookups (2D) like ref_GlassEnergy: thickness x energy type
import {
  React,
  useEffect,
  useState,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';

const Lookup2DTablesTab = () => {
  const [lookups, setLookups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLookup, setSelectedLookup] = useState(null);
  const [openLookupDialog, setOpenLookupDialog] = useState(false);
  const [editLookupData, setEditLookupData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 15 });

  const fetchLookups = () => {
    setLoading(true);
    authFetch('/pricing/lookups-2d')
      .then(data => { setLookups(data); setLoading(false); })
      .catch(err => { console.error('Failed to fetch 2D lookups:', err); setLoading(false); });
  };

  useEffect(() => { fetchLookups(); }, []);

  const handleSaveLookup = async (formData) => {
    try {
      let updatedRow;
      if (editLookupData?.id) {
        updatedRow = await authFetchRequest(`/pricing/lookups-2d/${editLookupData.id}`, formData, 'PUT');
        setLookups(prev => prev.map(row => row.id === editLookupData.id ? updatedRow : row));
        if (selectedLookup?.id === editLookupData.id) setSelectedLookup(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/lookups-2d', formData, 'POST');
        setLookups(prev => [...prev, updatedRow]);
      }
      setOpenLookupDialog(false);
      setEditLookupData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save 2D lookup');
    }
  };

  const handleDeleteLookup = async () => {
    if (!selectedLookup) return;
    const confirmed = await confirmC('Are you sure you want to delete this 2D lookup and all its data?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/lookups-2d/${selectedLookup.id}`, null, 'DELETE');
      setLookups(prev => prev.filter(row => row.id !== selectedLookup.id));
      setSelectedLookup(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete 2D lookup');
    }
  };

  const lookupColumns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'code', headerName: 'Code', width: 200 },
    { field: 'name', headerName: 'Name', width: 250 },
    { field: 'row_label', headerName: 'Row Label', width: 120 },
    { field: 'column_label', headerName: 'Column Label', width: 120 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>2D Lookup Tables</Typography>

      {/* Lookups List Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Lookup Definitions</Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button variant="contained" size="small" onClick={() => { setEditLookupData(null); setOpenLookupDialog(true); }}>
            Add Lookup
          </Button>
          <Button variant="contained" color="secondary" size="small" onClick={() => { setEditLookupData(selectedLookup); setOpenLookupDialog(true); }} disabled={!selectedLookup}>
            Edit Lookup
          </Button>
          <Button variant="outlined" color="error" size="small" onClick={handleDeleteLookup} disabled={!selectedLookup}>
            Delete Lookup
          </Button>
        </Stack>

        <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <span style={{ fontSize: '0.85em', color: '#555' }}>
            2D lookups are matrix tables where you look up a value by matching both a row key and a column key.
            Click a lookup to view/edit its matrix data below.
          </span>
        </Box>

        {loading ? <CircularProgress size={24} /> : (
          <DataGrid
            autoHeight
            rowHeight={30}
            rows={lookups}
            columns={lookupColumns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 15, 25]}
            onRowClick={(params) => setSelectedLookup(params.row)}
            sx={{
              '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
              '& .MuiDataGrid-row': { cursor: 'pointer' }
            }}
            getRowClassName={(params) => params.row.id === selectedLookup?.id ? 'Mui-selected' : ''}
          />
        )}
      </Paper>

      {/* Matrix Editor for selected lookup */}
      {selectedLookup && (
        <Lookup2DMatrixEditor lookup={selectedLookup} />
      )}

      {/* Lookup Dialog */}
      <Lookup2DDialog
        open={openLookupDialog}
        data={editLookupData}
        onClose={() => { setOpenLookupDialog(false); setEditLookupData(null); }}
        onSave={handleSaveLookup}
      />
    </Box>
  );
};

const Lookup2DDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({ code: '', name: '', description: '', row_label: 'Row', column_label: 'Column', active: true });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        row_label: data.row_label || 'Row',
        column_label: data.column_label || 'Column',
        active: data.active ?? true
      });
    } else {
      setFormData({ code: '', name: '', description: '', row_label: 'Row', column_label: 'Column', active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit 2D Lookup' : 'Add 2D Lookup'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., glass_energy_pricing" />
          <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., Glass Energy Pricing Matrix" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Row Label" value={formData.row_label} onChange={(e) => setFormData({ ...formData, row_label: e.target.value })} fullWidth placeholder="e.g., Thickness" />
            <TextField label="Column Label" value={formData.column_label} onChange={(e) => setFormData({ ...formData, column_label: e.target.value })} fullWidth placeholder="e.g., Energy Type" />
          </Stack>
          <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// Matrix editor component for 2D lookups
const Lookup2DMatrixEditor = ({ lookup }) => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRowKey, setNewRowKey] = useState('');
  const [newColKey, setNewColKey] = useState('');

  const fetchMatrixData = async () => {
    setLoading(true);
    try {
      const [rowsData, colsData, cellsData] = await Promise.all([
        authFetch(`/pricing/lookups-2d/${lookup.id}/rows`),
        authFetch(`/pricing/lookups-2d/${lookup.id}/columns`),
        authFetch(`/pricing/lookups-2d/${lookup.id}/cells`)
      ]);
      setRows(rowsData);
      setColumns(colsData);
      setCells(cellsData);
    } catch (err) {
      console.error('Failed to fetch matrix data:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchMatrixData(); }, [lookup.id]);

  const handleAddRow = async () => {
    if (!newRowKey.trim()) return;
    try {
      const newRow = await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/rows`, { key_value: newRowKey.trim(), sort_order: rows.length }, 'POST');
      setRows(prev => [...prev, newRow]);
      setNewRowKey('');
    } catch (err) {
      console.error('Failed to add row:', err);
      alertC('Failed to add row');
    }
  };

  const handleAddColumn = async () => {
    if (!newColKey.trim()) return;
    try {
      const newCol = await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/columns`, { key_value: newColKey.trim(), sort_order: columns.length }, 'POST');
      setColumns(prev => [...prev, newCol]);
      setNewColKey('');
    } catch (err) {
      console.error('Failed to add column:', err);
      alertC('Failed to add column');
    }
  };

  const handleDeleteRow = async (rowId) => {
    try {
      await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/rows/${rowId}`, null, 'DELETE');
      setRows(prev => prev.filter(r => r.id !== rowId));
      setCells(prev => prev.filter(c => c.row_id !== rowId));

    } catch (err) {
      console.error('Failed to delete row:', err);
      alertC('Failed to delete row');
    }
  };

  const handleDeleteColumn = async (colId) => {
    try {
      await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/columns/${colId}`, null, 'DELETE');
      setColumns(prev => prev.filter(c => c.id !== colId));
      setCells(prev => prev.filter(c => c.column_id !== colId));

    } catch (err) {
      console.error('Failed to delete column:', err);
      alertC('Failed to delete column');
    }
  };

  const getCellValue = (rowId, colId) => {
    const cell = cells.find(c => c.row_id === rowId && c.column_id === colId);
    return cell?.value || '';
  };

  const handleCellChange = async (rowId, colId, value) => {
    const existingCell = cells.find(c => c.row_id === rowId && c.column_id === colId);
    try {
      if (existingCell) {
        const updated = await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/cells/${existingCell.id}`, { value }, 'PUT');
        setCells(prev => prev.map(c => c.id === existingCell.id ? updated : c));
      } else {
        const newCell = await authFetchRequest(`/pricing/lookups-2d/${lookup.id}/cells`, { row_id: rowId, column_id: colId, value }, 'POST');
        setCells(prev => [...prev, newCell]);
      }

    } catch (err) {
      console.error('Failed to update cell:', err);
    }
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Matrix: {lookup.name} ({lookup.code})
      </Typography>

      {/* Add Row/Column controls */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" label={`Add ${lookup.row_label || 'Row'}`} value={newRowKey} onChange={(e) => setNewRowKey(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddRow()} />
        <Button variant="outlined" size="small" onClick={handleAddRow}>+ Row</Button>
        <TextField size="small" label={`Add ${lookup.column_label || 'Column'}`} value={newColKey} onChange={(e) => setNewColKey(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddColumn()} />
        <Button variant="outlined" size="small" onClick={handleAddColumn}>+ Column</Button>
      </Stack>

      {/* Matrix Table */}
      {rows.length === 0 && columns.length === 0 ? (
        <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, textAlign: 'center', color: '#666' }}>
          No rows or columns defined yet. Add rows and columns above to build the matrix.
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #ddd', padding: '6px 10px', backgroundColor: '#f5f5f5' }}>
                  {lookup.row_label || 'Row'} / {lookup.column_label || 'Col'}
                </th>
                {columns.map(col => (
                  <th key={col.id} style={{ border: '1px solid #ddd', padding: '6px 10px', backgroundColor: '#f5f5f5', whiteSpace: 'nowrap' }}>
                    {col.key_value}
                    <Button size="small" sx={{ minWidth: 24, ml: 0.5, p: 0 }} color="error" onClick={() => handleDeleteColumn(col.id)}>×</Button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <td style={{ border: '1px solid #ddd', padding: '6px 10px', backgroundColor: '#f5f5f5', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    {row.key_value}
                    <Button size="small" sx={{ minWidth: 24, ml: 0.5, p: 0 }} color="error" onClick={() => handleDeleteRow(row.id)}>×</Button>
                  </td>
                  {columns.map(col => (
                    <td key={col.id} style={{ border: '1px solid #ddd', padding: '2px' }}>
                      <input
                        type="text"
                        defaultValue={getCellValue(row.id, col.id)}
                        onBlur={(e) => handleCellChange(row.id, col.id, e.target.value)}
                        style={{ width: '70px', border: 'none', padding: '4px', textAlign: 'center' }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
};

export default Lookup2DTablesTab;
